import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import type {Item} from '@react-stately/collections';
import type {AriaMenuProps} from '@react-aria/menu';
import cx from 'classnames';

import {Button} from '../../elements/Button/Button';
import {Icon, IconTypes} from '../../elements/Icon/Icon';
import {TextHeading} from '../../elements/TextHeading/TextHeading';
import {FontVariant} from '../../../../design-tokens/constants';

import {InnerAriaMenu} from './InnerAriaMenu';
import type {
    AriaMenuItem,
    AriaSubMenu,
    AriaSubMenuContentHelpers,
} from './AriaMenu.types';

import styles from './AriaMenu.module.scss';

type NavLevel = {
    title?: React.ReactNode;
    ariaLabel?: string;
    titleTestId?: string;
    testId?: string;
    backButtonLabel?: string;
    backButtonTestId?: string;
} & (
    | {items: AriaMenuItem[]; content?: undefined}
    | {
          content:
              | React.ReactNode
              | ((helpers: AriaSubMenuContentHelpers) => React.ReactNode);
          items?: undefined;
      }
);

const subMenuToLevel = (
    subMenu: AriaSubMenu,
    parentItem: AriaMenuItem,
): NavLevel => {
    const common = {
        title: subMenu.title,
        ariaLabel:
            typeof parentItem.label === 'string'
                ? parentItem.label
                : parentItem.textValue,
        titleTestId: subMenu.titleTestId,
        testId: subMenu.testId,
        backButtonLabel: subMenu.backButtonLabel,
        backButtonTestId: subMenu.backButtonTestId,
    };
    return subMenu.items !== undefined
        ? {...common, items: subMenu.items}
        : {...common, content: subMenu.content};
};

/**
 * Manages in-place navigation between the root menu and any submenus. The
 * navigation stack is local state, so it resets whenever the popover unmounts
 * (i.e. when the menu closes).
 */
export const InnerAriaMenuNavigator: React.FC<{
    rootItems: AriaMenuItem[];
    headerContent?: React.ReactElement;
    menuProps: Partial<AriaMenuProps<typeof Item>>;
    close: () => void;
    testId?: string;
    menuClassName?: string;
    itemClassName?: string;
    backButtonLabel?: string;
    onGoBack?: () => void;
    showSubMenuIndicator?: boolean;
}> = ({
    rootItems,
    headerContent,
    menuProps,
    close,
    testId,
    menuClassName,
    itemClassName,
    backButtonLabel,
    onGoBack,
    showSubMenuIndicator,
}) => {
    // Store the navigation path as item keys rather than snapshotting the
    // submenu levels themselves. Deriving the levels from `rootItems` on every
    // render keeps the rendered submenu (its title, items and content) in sync
    // when the source items change while the menu is open — e.g. changing the
    // language from within a submenu updates the header immediately instead of
    // showing a stale snapshot from when the submenu was opened.
    const [path, setPath] = useState<string[]>([]);
    const [restoreFocusKey, setRestoreFocusKey] = useState<string>();

    const stack = useMemo<NavLevel[]>(() => {
        const levels: NavLevel[] = [{items: rootItems}];
        let levelItems: AriaMenuItem[] | undefined = rootItems;
        for (const key of path) {
            const item = levelItems?.find(candidate => candidate.key === key);
            if (!item?.subMenu) {
                // The path is no longer resolvable (e.g. the item was removed),
                // so stop at the deepest level we can still render.
                break;
            }
            const level = subMenuToLevel(item.subMenu, item);
            levels.push(level);
            levelItems = level.items;
        }
        return levels;
    }, [rootItems, path]);

    const resolvedPathLength = stack.length - 1;
    useEffect(() => {
        if (path.length > resolvedPathLength) {
            setPath(path.slice(0, resolvedPathLength));
        }
    }, [path, resolvedPathLength]);

    const currentLevel = stack[stack.length - 1] ?? {items: rootItems};
    const isSubMenu = stack.length > 1;
    const isContentSubMenu = isSubMenu && currentLevel.content !== undefined;
    const hasSubMenuTitle = isSubMenu && Boolean(currentLevel.title);
    const subMenuTitleId = useId();

    const contentRef = useRef<HTMLDivElement>(null);

    const handleSelect = useCallback(
        (item: AriaMenuItem) => {
            if (item.isDisabled) {
                return;
            }
            if (item.subMenu) {
                item.onItemClick?.();
                setPath(prev => [...prev, item.key]);
                return;
            }
            item.onItemClick?.();
            if (item.closeMenuOnItemClick !== false) {
                close();
            }
        },
        [close],
    );

    const handleFocusRestored = useCallback(() => {
        setRestoreFocusKey(undefined);
    }, []);

    const goBack = useCallback(() => {
        const poppedKey = path[path.length - 1];
        if (poppedKey !== undefined) {
            setRestoreFocusKey(poppedKey);
            setPath(path.slice(0, -1));
        }
        onGoBack?.();
    }, [onGoBack, path]);

    // Allow the "back" arrow key (ArrowLeft in LTR, ArrowRight in RTL) to
    // return from a React content submenu, mirroring the back button. We only
    // act when focus is on the content container itself so that arrow keys
    // inside interactive content (inputs, selects, sliders, ...) keep working.
    const handleContentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) {
            return;
        }
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
            return;
        }
        const isRtl = getComputedStyle(e.currentTarget).direction === 'rtl';
        const backKey = isRtl ? 'ArrowRight' : 'ArrowLeft';
        if (e.key === backKey) {
            e.preventDefault();
            goBack();
        }
    };

    // When navigating into a React content submenu there is no menu to receive
    // focus (unlike an items submenu, which autofocuses its first item), so move
    // focus to the content container to keep keyboard navigation working. A
    // content submenu is always a leaf, so entering one is a false -> true
    // transition; keying on this flag focuses on navigation but not when the
    // source items are rebuilt (e.g. on a language change), avoiding focus theft.
    useEffect(() => {
        if (isContentSubMenu) {
            contentRef.current?.focus();
        }
    }, [isContentSubMenu]);

    return (
        <div className={styles.innerMenuNavigator}>
            {isSubMenu ? (
                <div className={styles.innerMenuHeader}>
                    <Button
                        aria-label={
                            currentLevel.backButtonLabel ??
                            backButtonLabel ??
                            'Go back'
                        }
                        onClick={goBack}
                        size="none"
                        variant="translucent"
                        data-testid={currentLevel.backButtonTestId}
                        className={styles.innerMenuBackButton}
                    >
                        <Icon
                            size="small"
                            source={IconTypes.IconArrowLeftRegular}
                            className={styles.innerMenuBackIcon}
                        />
                    </Button>
                    {currentLevel.title && (
                        <TextHeading
                            id={subMenuTitleId}
                            htmlTag="h2"
                            fontVariant={FontVariant.H5}
                            data-testid={currentLevel.titleTestId}
                        >
                            {currentLevel.title}
                        </TextHeading>
                    )}
                </div>
            ) : (
                headerContent && <div>{headerContent}</div>
            )}
            <div className={styles.innerMenuContentContainer}>
                {currentLevel.items !== undefined ? (
                    <InnerAriaMenu
                        items={currentLevel.items}
                        onSelect={handleSelect}
                        onClose={close}
                        ariaProps={isSubMenu ? undefined : menuProps}
                        ariaLabel={
                            isSubMenu && !hasSubMenuTitle
                                ? currentLevel.ariaLabel
                                : undefined
                        }
                        ariaLabelledBy={
                            hasSubMenuTitle ? subMenuTitleId : undefined
                        }
                        autoFocus={
                            restoreFocusKey !== undefined
                                ? false
                                : isSubMenu
                                  ? 'first'
                                  : undefined
                        }
                        restoreFocusKey={restoreFocusKey}
                        onFocusRestored={handleFocusRestored}
                        testId={isSubMenu ? currentLevel.testId : testId}
                        menuClassName={menuClassName}
                        itemClassName={itemClassName}
                        showSubMenuIndicator={showSubMenuIndicator}
                        onNavigateBack={isSubMenu ? goBack : undefined}
                    />
                ) : (
                    // biome-ignore lint/a11y/noStaticElementInteractions: the content container is a focus target (tabIndex -1) that only listens for the back-navigation arrow key; it is not a click target, so an interactive role would be misleading
                    <div
                        ref={contentRef}
                        tabIndex={-1}
                        onKeyDown={handleContentKeyDown}
                        className={cx(styles.innerMenuContent, menuClassName)}
                        data-testid={currentLevel.testId}
                    >
                        {typeof currentLevel.content === 'function'
                            ? currentLevel.content({close, goBack})
                            : currentLevel.content}
                    </div>
                )}
            </div>
        </div>
    );
};
